/**
 * Note: lot's of hard-coded things in this chart, because it's the only one.
 * Please adjust, if it turns out we need it more generically.
 */ import * as stylex from "@stylexjs/stylex";
import { Delaunay } from "d3-delaunay";
import { format } from "d3-format";
import * as scale from "d3-scale";
import * as shape from "d3-shape";
import { flow } from "fp-ts/lib/function";
import { pipe } from "fp-ts/lib/pipeable";
import { motion } from "framer-motion";
import React from "react";
import ColorLegend from "../charts/lib/ColorLegend";
import { ChartTooltip, TooltipGridBody } from "../components/tooltip";
import { useContentRect, usePointerPosition } from "../hooks";
import { extentNumber, insertionOrderSet } from "../lib";
import * as M from "../materials";
import { colors } from "../materials/consts.stylex";
import { layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { An, Ar, O, Ord, R } from "../prelude";
const formatPct = format(".1%");
const formatPctRound = format(".0%");
const styles = {
    axisLabel: {
        kDwRjp: "x13ylgtg",
        $$css: true
    },
    yearLabel: {
        kDwRjp: "x1oxvhtd",
        $$css: true
    },
    tickLabel: {
        kDwRjp: "x13ylgtg",
        $$css: true
    },
    annotationLabel: {
        k63SB2: "x117nqv4",
        kDwRjp: "xjqwmh7",
        kCrYw0: "x1x0jnz8",
        kjVXCG: "xqvyd8v",
        kfJifR: "xnpr05h",
        kU5bRw: "x5lxx2x",
        kJdQPc: "x1bivah8",
        $$css: true
    }
};
const Scatterplot = React.memo(Scatterplot_);
function Scatterplot_(p) {
    const [pointerPositionRef, pointerPosition] = usePointerPosition();
    const margin = React.useMemo(()=>({
            top: 30,
            right: 16,
            bottom: 38,
            left: 40
        }), []);
    const outerHeight = p.height + margin.top + margin.bottom;
    const innerHeight = p.height;
    const outerWidth = p.width;
    const innerWidth = p.width - margin.left - margin.right;
    const { xScale, xPos, yScale, yPos, timeSteps } = React.useMemo(()=>{
        const xDomain = p.xTicks ? p.xTicks : extentNumber(p.data, p.getX);
        const xScale = scale.scaleLinear().domain(xDomain).range([
            0,
            innerWidth
        ]);
        const xPos = (x)=>xScale(p.getX(x));
        const yDomain = p.yTicks ? p.yTicks : extentNumber(p.data, p.getY);
        const yScale = scale.scaleLinear().domain(yDomain).range([
            innerHeight,
            0
        ]);
        const yPos = (x)=>yScale(p.getY(x));
        const timeSteps = Ar.sort(Ord.ordNumber)(Array.from(new Set(p.data.map((x)=>x.year)).values()));
        const timeExtent = [
            timeSteps[0],
            timeSteps[timeSteps.length - 1]
        ];
        const timeScale = (x)=>timeSteps.indexOf(x);
        timeScale.invert = (x)=>timeSteps[x];
        return {
            xDomain,
            xScale,
            xPos,
            yDomain,
            yScale,
            yPos,
            timeScale,
            timeSteps,
            timeExtent
        };
    // eslint-disable-next-line
    }, [
        innerHeight,
        innerWidth,
        p.getX,
        p.getY,
        p.data
    ]);
    const [colorScale, colorLegendValues] = useColorScale({
        data: p.data,
        getColor: p.getColor
    });
    const yearlyData = React.useMemo(()=>yearlyDataPerGroup({
            getGroup: p.getGroup,
            data: p.data,
            timeSteps
        }), [
        p.getGroup,
        p.data,
        timeSteps
    ]);
    const snapshotData = React.useMemo(()=>{
        return pipe(yearlyData, R.filterMap(flow(Ar.findFirst((x)=>x.year === p.selectedYear))), R.collect((_, xs)=>xs));
    }, [
        yearlyData,
        p.selectedYear
    ]);
    const delaunay = React.useMemo(()=>Delaunay.from(snapshotData, xPos, yPos), [
        snapshotData,
        xPos,
        yPos
    ]);
    const hoveredDatum = React.useMemo(()=>{
        return pipe(pointerPosition, O.chain((pos)=>{
            const i = delaunay.find(pos.x, pos.y);
            const datum = snapshotData[i];
            const cx = xPos(datum);
            const cy = yPos(datum);
            return distance([
                pos.x,
                pos.y
            ], [
                cx,
                cy
            ]) <= 20 ? O.some(datum) : O.none;
        }));
    }, [
        delaunay,
        pointerPosition,
        snapshotData,
        xPos,
        yPos
    ]);
    const timeseriesData = React.useMemo(()=>{
        return pipe(pipe(hoveredDatum, O.map((x)=>[
                p.getGroup(x)
            ].concat(pipe(p.highlightedGroups, O.fold(()=>[], (xs)=>xs))))), O.alt(()=>p.highlightedGroups), O.map((xs)=>{
            return pipe(yearlyData, R.filterWithIndex((k)=>xs.includes(k)), R.filterMap((xs)=>An.fromArray(xs.filter((x)=>x.year <= p.selectedYear))), R.collect((_, vs)=>vs));
        }));
    // eslint-disable-next-line
    }, [
        p.highlightedGroups,
        yearlyData,
        hoveredDatum,
        p.getGroup,
        p.selectedYear
    ]);
    const lineGenerator = React.useMemo(()=>{
        return shape.line().x(xPos).y(yPos).curve(shape.curveCatmullRom);
    }, [
        xPos,
        yPos
    ]);
    const displaySelectedYear = p.displaySelectedYear ?? true;
    const displayColorLegend = p.displayColorLegend ?? true;
    return <div>
      <svg height={outerHeight} width={outerWidth}>
        <g key={"axes"} transform={`translate(${margin.left}, ${margin.top})`}>
          <text {...stylex.props(typography.axis, styles.axisLabel)} x={0} dy={"-0.6em"}>
            {p.yLabel}
          </text>
          <text {...stylex.props(typography.axis, styles.axisLabel)} x={innerWidth} y={innerHeight} dy={"2.5em"} textAnchor={"end"}>
            {p.xLabel}
          </text>
          <XTicks formatTick={p.formatX} xScale={xScale} height={innerHeight}/>
          <YTicks formatTick={p.formatY} yScale={yScale} width={innerWidth}/>

          {p.annotations && <Annotations xScale={xScale} yScale={yScale} annotations={p.annotations} height={innerHeight} width={innerWidth}/>}

          {displaySelectedYear && <text textAnchor={"end"}>
              <tspan x={innerWidth - 12} y={innerHeight} dy={"-48px"} {...stylex.props(typography.meta, styles.yearLabel)}>
                Year
              </tspan>
              <tspan x={innerWidth - 12} y={innerHeight} dy={"-0.6em"} {...stylex.props(typography.heading1, styles.yearLabel)}>
                {p.selectedYear}
              </tspan>
            </text>}
        </g>

        <g key={"inner"} transform={`translate(${margin.left}, ${margin.top})`}>
          {snapshotData.map((datum)=>{
        const cx = xPos(datum);
        const cy = yPos(datum);
        return <motion.circle key={p.getGroup(datum)} r={5} fill={colorScale(p.getColor(datum))} opacity={0.25} initial={{
            cx,
            cy
        }} animate={{
            cx,
            cy
        }} transition={{
            duration: 1,
            ease: "linear"
        }}/>;
    })}
          {pipe(timeseriesData, O.map((xxs)=>xxs.map((xs, i)=><React.Fragment key={i}>
                  <motion.path d={lineGenerator(xs) || ""} stroke={colorScale(p.getColor(xs[0]))} strokeWidth={1} fill="transparent"/>
                  {xs.map((datum)=>{
                const cx = xPos(datum);
                const cy = yPos(datum);
                const r = datum.year === p.selectedYear ? 5 : 2;
                return <motion.circle key={`${p.getGroup(datum)}-${datum.year}`} fill={colorScale(p.getColor(datum))} initial={{
                    cx,
                    cy,
                    r
                }} animate={{
                    cx,
                    cy,
                    r
                }}/>;
            })}
                </React.Fragment>)), O.toNullable)}
          {pipe(hoveredDatum, O.map((datum)=><Highlight key="highlight" datum={datum} getX={p.getX} getY={p.getY} getColor={p.getColor} xPos={xPos} yPos={yPos} formatTitle={p.formatGroup} formatX={p.formatX} formatY={p.formatY} xLabel={p.xLabel} yLabel={p.yLabel} colorScale={colorScale}/>), O.toNullable)}
          <rect ref={pointerPositionRef} x={0} y={0} className="x1ypdohk" width={innerWidth} height={innerHeight} fill="transparent" onClick={()=>pipe(hoveredDatum, O.map((x)=>p.onSelect && p.onSelect(x)))}/>
        </g>
      </svg>
      {displayColorLegend && <div style={{
        marginLeft: margin.left
    }}>
          <ColorLegend maxWidth={innerWidth} inline values={colorLegendValues}/>
        </div>}
    </div>;
}
export function ScatterplotAuto(props) {
    const [ref, contentRect] = useContentRect();
    return <div ref={ref} {...stylex.props(layout.fullWidth)}>
      {contentRect.width > 0 && <Scatterplot {...props} width={contentRect.width}/>}
    </div>;
}
// -----------------------------------------------------------------------------
function useColorScale({ ...p }) {
    const domainColor = React.useMemo(()=>insertionOrderSet(p.data, p.getColor), [
        p.data,
        p.getColor
    ]);
    const colorScale = React.useMemo(()=>scale.scaleOrdinal(M.colorRanges.discrete).domain(domainColor), [
        domainColor
    ]);
    const colorLegendValues = React.useMemo(()=>{
        return domainColor.map((key)=>({
                color: colorScale(key),
                label: key
            }));
    }, [
        colorScale,
        domainColor
    ]);
    return [
        colorScale,
        colorLegendValues
    ];
}
// -----------------------------------------------------------------------------
const XTicks = React.memo(({ xScale, formatTick = formatPctRound, height })=>{
    const ticks = xScale.domain();
    return <>
        {ticks.map((tick, i)=>{
        const x = xScale(tick) || 0;
        return <React.Fragment key={`xaxis-${tick}`}>
              <line className="xnpr05h x9zv0gs xc03x0k" x1={x} x2={x} y1={0} y2={height + 4}/>
              <text {...stylex.props(typography.axis, styles.tickLabel)} x={x} y={height} dy={"1.2em"} textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}>
                {formatTick(tick)}
              </text>
            </React.Fragment>;
    })}
      </>;
});
const YTicks = React.memo(({ yScale, formatTick = formatPctRound, width })=>{
    const ticks = yScale.domain();
    return <>
        {ticks.map((tick)=>{
        const y = yScale(tick) || 0;
        return <React.Fragment key={`xaxis-${tick}`}>
              <line className="xnpr05h x9zv0gs xc03x0k" x1={-4} x2={width} y1={y} y2={y}/>
              <text {...stylex.props(typography.axis, styles.tickLabel)} x={-8} y={y} textAnchor={"end"} dominantBaseline="middle">
                {formatTick(tick)}
              </text>
            </React.Fragment>;
    })}
      </>;
});
const Annotations = React.memo(({ xScale, formatX = formatPctRound, yScale, formatY = formatPctRound, annotations, height, width })=>{
    return <>
        <g key="x-annotations">
          {annotations.x.map((pos, i)=><React.Fragment key={i}>
              <line x1={xScale(pos)} x2={xScale(pos)} y1={0} y2={height} strokeDasharray={"6 4"} stroke={M.unescoDarkBlue} strokeWidth={2} opacity={0.2}/>
              <text {...stylex.props(typography.axis, styles.tickLabel)} x={xScale(pos)} y={height} dy={"1.2em"} textAnchor={"middle"}>
                {formatX(pos)}
              </text>
            </React.Fragment>)}
        </g>

        <g key="y-annotations">
          {annotations.y.map((pos, i)=><React.Fragment key={i}>
              <line y1={yScale(pos)} y2={yScale(pos)} x1={0} x2={width} strokeDasharray={"6 4"} stroke={M.unescoDarkBlue} strokeWidth={2} opacity={0.2}/>
              <text {...stylex.props(typography.axis, styles.tickLabel)} x={-8} y={yScale(pos)} textAnchor={"end"} dominantBaseline="middle">
                {formatY(pos)}
              </text>
            </React.Fragment>)}
        </g>

        {annotations.labels.map((label, i)=><text key={i} x={xScale(label.x)} y={yScale(label.y)} textAnchor="start" dx={label.dx} dy={label.dy} {...stylex.props(typography.meta, styles.annotationLabel)}>
            {label.label}
          </text>)}
      </>;
});
const Highlight = React.memo(Highlight_);
function Highlight_({ datum, getX, getY, getColor, xLabel, yLabel, xPos, yPos, formatX = formatPct, formatY = formatPct, formatTitle, colorScale }) {
    const cx = xPos(datum);
    const cy = yPos(datum);
    return <>
      <circle key="hovered" fill="transparent" stroke={colorScale(getColor(datum))} strokeWidth={5} opacity={0.5} cx={cx} cy={cy} r={9}/>
      <ChartTooltip title={`${formatTitle(datum)} (${datum.year})`} content={<TooltipGridBody rows={[
        [
            yLabel,
            formatY(getY(datum))
        ],
        [
            xLabel,
            formatX(getX(datum))
        ]
    ]}/>} placement="right-start" showOnCreate hideOnClick={false} arrow={false}>
        <circle cx={0} cy={16} r={1} fill={"transparent"}/>
      </ChartTooltip>
    </>;
}
// -----------------------------------------------------------------------------
/**
 * Fill in an array for every time step, carrying over data from previous years,
 * if a more recent one doesn't have data.
 */ function yearlyDataPerGroup(p) {
    return pipe(An.groupBy(p.getGroup)(p.data), R.map((xs)=>{
        const first = An.head(xs);
        const years = p.timeSteps.slice(p.timeSteps.indexOf(first.year));
        const lookup = pipe(xs, An.groupBy((x)=>`${x.year}`));
        let template = first;
        return years.map((year)=>{
            const x = lookup[year];
            if (x != null) {
                template = An.head(x);
                return template;
            } else {
                return {
                    ...template,
                    year
                };
            }
        });
    }));
}
function distance([x1, y1], [x2, y2]) {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}
